import type { MatchPhase, ProjectState } from "@livcg/schema";
import { Minus, Pause, Play, Plus, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useLiveClock } from "../shared/liveClock.js";
import { sendRaw } from "../shared/sendRaw.js";
import { Button, ConfirmDialog, cn, focusRing, Stat } from "../ui/index.js";
import { useGoalFlash } from "./overlayReturn.js";
import { PHASE_LABEL, PHASES } from "./phases.js";
import { TeamColorSwatch } from "./TeamColorSwatch.js";
import { extraBadgeTitle, useStagedExtraTime } from "./useStagedExtraTime.js";

function pad(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

type Props = {
  slug: string;
  state: ProjectState;
};

export function MatchDayHeader({ slug, state }: Props) {
  const m = state.match;
  const home = state.teams.home;
  const away = state.teams.away;

  const { flash } = useGoalFlash(slug);
  const adjustScore = (side: "home" | "away", delta: number) => {
    void sendRaw("score.adjust", { slug, side, delta });
    // SKORE parity: pressing + also puts the GOL tape on air (5s, then the previous mode).
    if (delta > 0) flash(side);
  };

  return (
    <header className="border-b border-divider bg-surface/40 px-8 py-6">
      {/* The content sits in a centered container so the teams do not drift to
          the edges on a wide screen. */}
      <div className="mx-auto grid w-full max-w-5xl grid-cols-[1fr_auto_1fr] items-center gap-8">
        <TeamSide
          slug={slug}
          editable={!!home}
          side="home"
          name={home?.name ?? "Home"}
          short={home?.shortName ?? "HOM"}
          color={home?.colorPrimary ?? "#5e6ad2"}
          score={m.homeScore}
          onAdjust={(d) => adjustScore("home", d)}
        />

        <Clock slug={slug} state={state} />

        <TeamSide
          slug={slug}
          editable={!!away}
          side="away"
          name={away?.name ?? "Away"}
          short={away?.shortName ?? "AWY"}
          color={away?.colorPrimary ?? "#ef4444"}
          score={m.awayScore}
          onAdjust={(d) => adjustScore("away", d)}
        />
      </div>
    </header>
  );
}

function Clock({ slug, state }: { slug: string; state: ProjectState }) {
  // The clock is a separate subscription — the per-second tick renders only this block.
  const m = useLiveClock(slug);

  // Single MM:SS input — accepts "45:23" or just "4523" (4-digit shorthand).
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>("");
  const [confirmReset, setConfirmReset] = useState(false);
  const extra = useStagedExtraTime(slug, state);

  const beginEdit = () => {
    setEditValue(`${pad(m.minute)}:${pad(m.second)}`);
    setEditing(true);
  };

  /** Parse the edit string into (minute, second).
   *  - "45:23"  → 45, 23
   *  - "4523"   → 45, 23  (last 2 chars are seconds)
   *  - "523"    → 5, 23
   *  - "23"     → 23, 0   (single chunk = minutes only)
   *  - "0"      → 0, 0
   */
  const parseEdit = (raw: string): { minute: number; second: number } => {
    const trimmed = raw.trim();
    let mm = 0;
    let ss = 0;
    if (trimmed.includes(":")) {
      const [mPart = "0", sPart = "0"] = trimmed.split(":");
      mm = Number(mPart) || 0;
      ss = Number(sPart) || 0;
    } else {
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length <= 2) {
        mm = Number(digits) || 0;
      } else {
        mm = Number(digits.slice(0, -2)) || 0;
        ss = Number(digits.slice(-2)) || 0;
      }
    }
    return {
      minute: Math.max(0, Math.min(130, Math.floor(mm))),
      second: Math.max(0, Math.min(59, Math.floor(ss))),
    };
  };

  const commitEdit = () => {
    const { minute, second } = parseEdit(editValue);
    void sendRaw("clock.set", {
      slug,
      minute,
      second,
      phase: m.phase,
      additionalTime: m.additionalTime,
      running: m.running,
    });
    setEditing(false);
  };

  const toggle = () => void sendRaw("clock.toggle", { slug });
  const doReset = () => {
    void sendRaw("clock.set", {
      slug,
      minute: 0,
      second: 0,
      phase: m.phase,
      additionalTime: 0,
      running: false,
    });
  };
  const adjust = (deltaSeconds: number) => void sendRaw("clock.adjust", { slug, deltaSeconds });
  const setPhase = (phase: MatchPhase) => void sendRaw("clock.setPhase", { slug, phase });

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-block h-2 w-2 rounded-full",
            m.running ? "bg-danger animate-pulse" : "bg-ink-faint",
          )}
        />
        <span className="text-[11px] uppercase tracking-[0.12em] text-ink-muted font-medium">
          {m.running ? "LIVE" : "PAUSE"} · /{slug}
        </span>
      </div>

      {/* Editable clock display — same visual size as the readout (88px
          font). Spinners are hidden globally in shared/styles.css; the ±
          buttons below cover stepping. Width follows content, but the
          parent grid column accepts it. */}
      {editing ? (
        <input
          type="text"
          inputMode="numeric"
          value={editValue}
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitEdit();
            }
            if (e.key === "Escape") setEditing(false);
          }}
          onBlur={commitEdit}
          placeholder="MM:SS"
          // Format hint lives in `title` so the layout stays a single 88px
          // line — same footprint as the un-clicked readout.
          title='"45:23" yoki "4523" · Enter — saqlash · Esc — bekor'
          // Explicit h-[88px] + py-0 + box-border so the input box matches
          // the un-clicked readout exactly (88px tall). Without these, the
          // browser's default vertical padding inflates it to ~120px.
          className="box-border h-[88px] w-[420px] py-0 rounded-md border-2 border-accent-strong bg-surface px-3 text-center font-display text-[88px] leading-[88px] font-bold tabular-nums tracking-[-0.04em] focus-visible:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={beginEdit}
          title="Vaqtni o'zgartirish uchun bosing"
          className={cn(
            focusRing,
            "font-display text-[88px] leading-none font-bold tabular-nums tracking-[-0.04em] hover:text-accent-strong transition-colors",
          )}
        >
          <span>{pad(m.minute)}</span>
          <span className={cn("mx-0.5", m.running ? "text-danger" : "text-ink-faint")}>:</span>
          <span>{pad(m.second)}</span>
        </button>
      )}

      {/* Extra time chip + stepper — the chip itself is the "on air" button. */}
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-[0.08em] text-ink-muted">Extra</span>
        <Button
          variant="secondary"
          size="xs"
          iconOnly
          aria-label="extra-vaqtni kamaytirish"
          disabled={extra.draft <= 0}
          onClick={extra.dec}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <button
          type="button"
          onClick={extra.send}
          disabled={!extra.canSend}
          title={extraBadgeTitle(extra.badge, extra.draft)}
          aria-label={extraBadgeTitle(extra.badge, extra.draft)}
          className={cn(
            "min-w-[36px] text-center rounded-md px-2 py-0.5 text-[12px] font-medium tabular-nums transition-colors",
            extra.badge === "staged" &&
              "cursor-pointer bg-warning/25 text-warning ring-2 ring-warning/70 animate-pulse",
            extra.badge === "sent" && "bg-warning/15 text-warning",
            extra.badge === "live" && "bg-live/20 text-live",
            extra.badge === "empty" && "bg-surface-elevated text-ink-muted",
          )}
        >
          +{extra.draft}
        </button>
        <Button
          variant="secondary"
          size="xs"
          iconOnly
          aria-label="extra-vaqtni oshirish"
          onClick={extra.inc}
        >
          <Plus className="h-3 w-3" />
        </Button>
        <label
          className={cn(
            "flex cursor-pointer items-center gap-1 text-[11px] text-ink-muted",
            extra.draft <= 0 && "cursor-not-allowed opacity-40",
          )}
          title="Soat ostidagi strip efirda turadimi (banner chiqmaydi)"
        >
          <input
            type="checkbox"
            className="h-3 w-3 accent-live"
            checked={extra.onAir}
            disabled={extra.draft <= 0}
            onChange={(e) => extra.setOnAir(e.target.checked)}
          />
          Strip
        </label>
      </div>

      {/* Adjust seconds row */}
      <div className="flex items-center gap-1">
        <AdjustBtn label="-10s" onClick={() => adjust(-10)} />
        <AdjustBtn label="-5s" onClick={() => adjust(-5)} />
        <AdjustBtn label="-1s" onClick={() => adjust(-1)} />
        <span className="w-2" />
        <AdjustBtn label="+1s" onClick={() => adjust(+1)} />
        <AdjustBtn label="+5s" onClick={() => adjust(+5)} />
        <AdjustBtn label="+10s" onClick={() => adjust(+10)} />
      </div>

      {/* Start / Reset / Phase */}
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant={m.running ? "secondary" : "success"}
          onClick={toggle}
          leadingIcon={
            m.running ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />
          }
        >
          {m.running ? "Pauza" : "Start"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setConfirmReset(true)}
          leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}
        >
          Reset
        </Button>
      </div>

      <select
        value={m.phase}
        onChange={(e) => setPhase(e.target.value as MatchPhase)}
        className="h-8 rounded-md border border-border bg-surface px-2 text-[12px] text-ink-soft hover:bg-surface-elevated"
      >
        {PHASES.map((p) => (
          <option key={p} value={p}>
            {PHASE_LABEL[p]}
          </option>
        ))}
      </select>

      <ConfirmDialog
        open={confirmReset}
        title="Vaqtni resetlash"
        description={`Vaqtni ${pad(m.minute)}:${pad(m.second)}'dan 00:00 ga olib tashlashni tasdiqlaysizmi?\nBu amalni qaytarib bo'lmaydi.`}
        confirmLabel="Resetlash"
        onConfirm={() => {
          setConfirmReset(false);
          doReset();
        }}
        onCancel={() => setConfirmReset(false)}
      />
    </div>
  );
}

function AdjustBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="secondary" size="sm" className="font-mono text-ink-soft" onClick={onClick}>
      {label}
    </Button>
  );
}

function TeamSide({
  slug,
  editable,
  side,
  name,
  short,
  color,
  score,
  onAdjust,
}: {
  slug: string;
  editable: boolean;
  side: "home" | "away";
  name: string;
  short: string;
  color: string;
  score: number;
  onAdjust: (delta: number) => void;
}) {
  const align = side === "home" ? "items-start" : "items-end";
  const dir = side === "home" ? "" : "flex-row-reverse";
  return (
    <div className={cn("flex flex-col gap-3", align)}>
      <div className={cn("flex items-center gap-3", dir)}>
        <TeamColorSwatch slug={slug} side={side} color={color} editable={editable} className="h-9 w-9" />
        <div className={cn("min-w-0", side === "away" && "text-right")}>
          <div className="t-mono">{side === "home" ? "Home" : "Away"}</div>
          <div className="text-[18px] font-semibold leading-tight truncate text-ink tracking-[-0.01em]">
            {name}
          </div>
          <div className="text-[12px] text-ink-muted">{short}</div>
        </div>
      </div>

      <div className={cn("flex items-center gap-3", dir)}>
        <Button
          variant="secondary"
          size="lg"
          iconOnly
          aria-label={`${name} hisobini −1`}
          onClick={() => onAdjust(-1)}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Stat value={score} />
        <Button
          variant="secondary"
          size="lg"
          iconOnly
          aria-label={`${name} hisobini +1`}
          onClick={() => onAdjust(+1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
